import React from 'react';
import {
  X,
  ExternalLink,
  ChevronRight,
  Shield,
  Key,
  Globe,
  Mail,
  AlertCircle,
  CheckCircle2,
  Lock,
  Server
} from 'lucide-react';

const GmailGuide = () => (
  <div className="space-y-6 text-gray-700">
    <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 flex gap-3">
      <InfoIcon className="text-blue-500 shrink-0 mt-0.5" size={20} />
      <p className="text-sm">
        This guide will walk you through configuring Gmail email settings in AptiView using OAuth 2.0 Authorization Code Flow.
      </p>
    </div>

    <div className="space-y-8">
      {/* Step 1 */}
      <div className="relative pl-8 pb-8 border-l-2 border-gray-100 last:border-0 last:pb-0">
        <div className="absolute left-[-17px] top-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-blue-200">
          1
        </div>
        <div>
          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Globe size={18} className="text-blue-500" />
            Create a Google Cloud Project
          </h3>
          <ul className="space-y-2 text-sm list-disc pl-5">
            <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium inline-flex items-center gap-1">Google Cloud Console <ExternalLink size={12} /></a></li>
            <li>Click on the project dropdown and select <strong>"New Project"</strong>.</li>
            <li>Enter a name (e.g., "AptiView Gmail Service") and click <strong>"Create"</strong>.</li>
          </ul>
        </div>
      </div>

      {/* Step 2 */}
      <div className="relative pl-8 pb-8 border-l-2 border-gray-100 last:border-0 last:pb-0">
        <div className="absolute left-[-17px] top-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-blue-200">
          2
        </div>
        <div>
          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Server size={18} className="text-blue-500" />
            Enable Gmail API
          </h3>
          <ul className="space-y-2 text-sm list-disc pl-5">
            <li>In the left sidebar, go to <strong>"APIs & Services"</strong> &gt; <strong>"Library"</strong>.</li>
            <li>Search for <strong>"Gmail API"</strong> and click <strong>"Enable"</strong>.</li>
          </ul>
        </div>
      </div>

      {/* Step 3 */}
      <div className="relative pl-8 pb-8 border-l-2 border-gray-100 last:border-0 last:pb-0">
        <div className="absolute left-[-17px] top-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-blue-200">
          3
        </div>
        <div>
          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Shield size={18} className="text-blue-500" />
            Configure OAuth Consent Screen
          </h3>
          <ul className="space-y-2 text-sm list-disc pl-5">
            <li>Go to <strong>"OAuth consent screen"</strong> in the left menu.</li>
            <li>Select <strong>"External"</strong> user type and click <strong>"Create"</strong>.</li>
            <li>Fill in App name, user support email, and developer contact info.</li>
            <li>On the <strong>"Scopes"</strong> page, add <code>https://www.googleapis.com/auth/gmail.send</code></li>
            <li>If in <strong>Testing</strong> mode, add your Gmail address as a <strong>Test user</strong>.</li>
          </ul>
        </div>
      </div>

      {/* Step 4 */}
      <div className="relative pl-8 pb-8 border-l-2 border-gray-100 last:border-0 last:pb-0">
        <div className="absolute left-[-17px] top-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-blue-200">
          4
        </div>
        <div>
          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Key size={18} className="text-blue-500" />
            Create Credentials
          </h3>
          <ul className="space-y-2 text-sm list-disc pl-5">
            <li>Go to <strong>"Credentials"</strong> &gt; <strong>"+ Create Credentials"</strong> &gt; <strong>"OAuth client ID"</strong>.</li>
            <li>Select <strong>"Web application"</strong> as the type.</li>
            <li>Add Authorized Redirect URI: <code className="bg-gray-100 px-1 rounded">https://interrogator.omnirises.com/api/companies/email-settings/oauth/callback/</code></li>
            <li>Copy your <strong>Client ID</strong> and <strong>Client Secret</strong>.</li>
          </ul>
        </div>
      </div>

      {/* Step 5 */}
      <div className="relative pl-8 pb-8 border-l-2 border-gray-100 last:border-0 last:pb-0">
        <div className="absolute left-[-17px] top-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-blue-200">
          5
        </div>
        <div>
          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
            <CheckCircle2 size={18} className="text-emerald-500" />
            Connect in AptiView
          </h3>
          <ul className="space-y-2 text-sm list-disc pl-5">
            <li>Enter the <strong>Client ID</strong> and <strong>Client Secret</strong> in the form.</li>
            <li>Click <strong>"Save Changes"</strong>, then <strong>"Test Connection"</strong>.</li>
            <li>Authorize the app in the Google popup.</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
);

const MicrosoftGuide = () => (
  <div className="space-y-6 text-gray-700">
    <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100 flex gap-3">
      <InfoIcon className="text-indigo-500 shrink-0 mt-0.5" size={20} />
      <p className="text-sm">
        This guide will walk you through configuring Microsoft Office 365 email settings in AptiView using Client Credentials Flow.
      </p>
    </div>

    <div className="space-y-8">
      {/* Step 1 */}
      <div className="relative pl-8 pb-8 border-l-2 border-gray-100 last:border-0 last:pb-0">
        <div className="absolute left-[-17px] top-0 w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-indigo-200">
          1
        </div>
        <div>
          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Globe size={18} className="text-indigo-500" />
            Register Application
          </h3>
          <ul className="space-y-2 text-sm list-disc pl-5">
            <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline font-medium inline-flex items-center gap-1">Azure Portal <ExternalLink size={12} /></a></li>
            <li>Open <strong>"Microsoft Entra ID"</strong> &gt; <strong>"App registrations"</strong>.</li>
            <li>Click <strong>"+ New registration"</strong> and enter a name.</li>
            <li>No Redirect URI is needed for Microsoft integration.</li>
          </ul>
        </div>
      </div>

      {/* Step 2 */}
      <div className="relative pl-8 pb-8 border-l-2 border-gray-100 last:border-0 last:pb-0">
        <div className="absolute left-[-17px] top-0 w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-indigo-200">
          2
        </div>
        <div>
          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Lock size={18} className="text-indigo-500" />
            Configure Permissions
          </h3>
          <ul className="space-y-2 text-sm list-disc pl-5">
            <li>In your app, go to <strong>"API permissions"</strong> &gt; <strong>"+ Add a permission"</strong>.</li>
            <li>Select <strong>"Microsoft Graph"</strong> &gt; <strong>"Application permissions"</strong>.</li>
            <li>Search for and check <strong>"Mail.Send"</strong>.</li>
            <li>Click <strong>"Grant admin consent for [Your Org]"</strong>. (Crucial step!)</li>
          </ul>
        </div>
      </div>

      {/* Step 3 */}
      <div className="relative pl-8 pb-8 border-l-2 border-gray-100 last:border-0 last:pb-0">
        <div className="absolute left-[-17px] top-0 w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-indigo-200">
          3
        </div>
        <div>
          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Key size={18} className="text-indigo-500" />
            Create Client Secret
          </h3>
          <ul className="space-y-2 text-sm list-disc pl-5">
            <li>Go to <strong>"Certificates & secrets"</strong> &gt; <strong>"+ New client secret"</strong>.</li>
            <li>Copy the <strong>Value</strong> immediately (it's hidden after leaving the page).</li>
          </ul>
        </div>
      </div>

      {/* Step 4 */}
      <div className="relative pl-8 pb-8 border-l-2 border-gray-100 last:border-0 last:pb-0">
        <div className="absolute left-[-17px] top-0 w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg shadow-indigo-200">
          4
        </div>
        <div>
          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
            <CheckCircle2 size={18} className="text-emerald-500" />
            Connect in AptiView
          </h3>
          <ul className="space-y-2 text-sm list-disc pl-5">
            <li>Copy the <strong>Application (client) ID</strong> and <strong>Directory (tenant) ID</strong> from the Overview page.</li>
            <li>Enter all three values (Client ID, Secret, Tenant ID) in AptiView.</li>
            <li>Click <strong>"Connect with Microsoft"</strong>.</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
);

const InfoIcon = ({ className, size }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

export default function EmailGuideModal({ isOpen, onClose, type }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm transition-all animate-in fade-in duration-300">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg ${type === 'gmail' ? 'bg-[#ea4335] shadow-red-100' : 'bg-[#00a4ef] shadow-blue-100'}`}>
              {type === 'gmail' ? <Mail size={20} /> : <Globe size={20} />}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {type === 'gmail' ? 'Gmail' : 'Microsoft 365'} Configuration Guide
              </h2>
              <p className="text-xs text-gray-500 font-medium tracking-wide uppercase mt-0.5">
                Setup Instructions
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
          {type === 'gmail' ? <GmailGuide /> : <MicrosoftGuide />}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 bg-gray-50/50 flex justify-end">
          <button
            onClick={onClose}
            className="px-8 py-2.5 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-gray-800 transition-all shadow-lg shadow-gray-200 active:scale-95"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Got it, thanks!
          </button>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e5e7eb;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #d1d5db;
        }
      `}} />
    </div>
  );
}
